/*
 * File: ApptCalendarDayView.js
 * Project: mp-mobile-app
 *
 * Created by Vamsi Sureddy
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Controls the Scheduling Calendar View
 */
import React, { Component } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  withStyles,
  IconButton
} from "@material-ui/core";
import ApptFormOptions from "../store/ApptFormOptions";
import SwipeableViews from "react-swipeable-views";
import { withRouter } from "react-router";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { getSnapshot } from "mobx-state-tree";
import "css/calendar.css";
import PatientIdStorage from "services/patients/store/PatientIdStore";
import UIProgress from "components/ui/UIProgress";
import { observer } from "mobx-react";
import UINotFound from "components/ui/UINotFound";
import UIStore from "../store/UIStore";
import ApptStore from "services/appointments/store/DataStore";
import PropTypes from "prop-types";
import NavigateNext from "@material-ui/icons/NavigateNext";
import NavigateBefore from "@material-ui/icons/NavigateBefore";
import ResourceCalendarView from "./ResourceCalendarView";
import { addMinutes, differenceInMinutes, parseISO } from "date-fns";
import { StoreContext } from "utils/useStore";
import Preferences from "services/preferences/Store";
import { parse } from "date-fns";

const styles = theme => ({
  resources: {
    display: "flex",
    alignContent: "center",
    borderBottom: "1px solid #ddd",
    paddingBottom: theme.spacing(1),
    flexShrink: 0
  },
  resourceForm: {
    height: theme.spacing(5)
  },
  calendarView: {
    width: "100%",
    flex: "1 1 100%",
    overflow: "auto"
  },
  calendar: {
    fontFamily:
      "HelveticaNeue-Light, Helvetica Neue Light, Helvetica Neue, Helvetica",
    width: "100%"
  }
});

class ApptCalendarDayView extends Component {
  state = {
    loading: true
  };
  static contextType = StoreContext;

  constructor(props) {
    super(props);
    this.selecting = false;
    this.resources = [];
  }

  get apptStore() {
    return this.context.getStore(ApptStore);
  }

  get uiStore() {
    return this.context.getStore(UIStore);
  }

  async componentDidMount() {
    const resources = await this.context
      .getStore(ApptFormOptions)
      .getResources();
    if (resources.isNullOrEmpty()) {
      this.setState({
        loading: false
      });
      return;
    }

    const prefs = this.context.getStore(Preferences);
    const filters = prefs.get("apptFilters");
    const min = prefs.get("scheduling.calendar.startTime");
    this.min = parse(min, "hh:mm a", new Date());

    const max = prefs.get("scheduling.calendar.endTime");
    this.max = parse(max, "hh:mm a", new Date());

    if (filters.resources === "all") {
      this.resources = resources;
    } else {
      this.resources = resources.filter(r => {
        return filters.resources.indexOf(r.resourceId) !== -1;
      });
    }

    const { resourceId } = this.uiStore;
    const selectedResIndex = this.resources.findIndex(
      res => res.resourceId === resourceId
    );
    if (this.resources.length > 0 && selectedResIndex === -1) {
      this.updateApptFromResource(this.resources[0]);
      this.setState({
        loading: false
      });
    } else {
      this.setState({
        loading: false
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("touchmove", this.preventScroll, {
      passive: false
    });
  }

  updateApptFromResource = resource => {
    // save to ui store
    this.uiStore.setResourceId(resource.resourceId);
    //updates the new appt model with the resource and resource provider (if applicable)
    this.apptStore.newAppt.update("resourceId", resource.resourceId);
    if (resource.providerId) {
      this.apptStore.newAppt.update("resourceProviderId", resource.providerId);
    } else this.apptStore.newAppt.update("resourceProviderId", null);
  };

  onSelecting = () => {
    this.selecting = true;
  };

  preventScroll = e => {
    if (this.selecting) {
      e.preventDefault();
    }
  };

  // called when a resource is selected from the dropdown
  handleResourceSelection = event => {
    // need to find the resource object by selected value
    const resource = this.resources.find(
      res => res.resourceId === event.target.value
    );
    this.updateApptFromResource(resource);
  };

  switchResource(idxChange) {
    //Find index of current resource in resources array
    const currentIdx = this.resources.findIndex(
      res => res.resourceId === this.uiStore.resourceId
    );
    //Change by idxChange (+1 or -1)
    const newIdx = currentIdx + idxChange;
    //Check if newIdx is a valid array index before using it to update resource being used
    if (newIdx + 1 > this.resources.length || newIdx < 0) return;
    this.updateApptFromResource(this.resources[newIdx]);
  }

  getEvents(resource) {
    let events = [];
    const appts = this.apptStore.appts.filter(
      appt => appt.resourceId === resource.resourceId
    );

    if (appts) {
      appts.forEach(appt => {
        let obj = Object.assign({}, getSnapshot(appt));
        obj["start"] = appt.startTime;
        obj["end"] = addMinutes(appt.startTime, appt.length);
        obj["patientName"] = appt.patientName;
        events.push(obj);
      });
    }

    return events;
  }

  getBackgroundEvents(resource) {
    let backgroundEvents = [];
    const transparencies = this.apptStore.transparencies;

    if (transparencies) {
      backgroundEvents = transparencies.map(transp => {
        let obj = Object.assign({}, transp);
        obj.start = parseISO(transp.start);
        obj.end = addMinutes(parseISO(transp.start), transp.duration);
        // Background events are filtered by resourceId, so if a transparency has the "all resources flag"
        // we need to set the resourceId to the current resource's id
        if (transp.allResources) {
          obj.resourceId = resource.resourceId;
        }
        return obj;
      });
    }

    return backgroundEvents;
  }

  onSelectSlot = slot => {
    this.selecting = false;
    const { newAppt } = this.apptStore;
    const start = slot.start;
    const end = slot.end;
    const length = differenceInMinutes(end, start);
    newAppt.update("appointmentDate", start);
    newAppt.update("appointmentTime", start);
    newAppt.update("start", start);
    newAppt.update("startTime", start);
    newAppt.update("length", length);
    newAppt.update("resourceId", slot.resourceId);
    this.uiStore.togglePatientSelection();
  };

  onSelectEvent = event => {
    if (event.blockName) return;
    const { history } = this.props;
    PatientIdStorage.setId(event.patientId);
    history.push("/appointments/" + event.appointmentId);
  };

  handleChangeIndex = index => {
    this.updateApptFromResource(this.resources[index]);
  };

  renderCalendar() {
    const { date, resourceId } = this.uiStore;
    this.apptDate = date;
    const selectedResIndex = this.resources.findIndex(
      res => res.resourceId === resourceId
    );
    return (
      date &&
      this.resources &&
      selectedResIndex !== -1 && (
        <div>
          <SwipeableViews
            index={selectedResIndex}
            onChangeIndex={this.handleChangeIndex}
          >
            {this.resources.map((res, idx) => {
              return (
                <ResourceCalendarView parent={this} key={idx} resource={res} />
              );
            })}
          </SwipeableViews>
        </div>
      )
    );
  }

  renderResources() {
    const { classes } = this.props;
    const { resourceId } = this.uiStore;
    return (
      <FormControl fullWidth className={classes.resourceForm}>
        <InputLabel shrink>Resources</InputLabel>
        <Select
          value={resourceId > 0 ? resourceId : ""}
          onChange={this.handleResourceSelection}
          label={"Resources"}
          fullWidth
          displayEmpty
        >
          {this.resources.map((res, idx) => {
            return (
              <MenuItem key={idx} value={res.resourceId}>
                {res.name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  }

  render() {
    const { classes } = this.props;
    const { loading } = this.state;
    if (loading) return <UIProgress />;
    if (this.resources.isNullOrEmpty())
      return (
        <div className="padding">
          <UINotFound message="No Resources" />
        </div>
      );
    return (
      <React.Fragment>
        <div className={classes.resources}>
          <IconButton
            className={classes.navButton}
            onClick={() => this.switchResource(-1)}
          >
            <NavigateBefore />
          </IconButton>
          {this.renderResources()}
          <IconButton
            className={classes.navButton}
            onClick={() => this.switchResource(1)}
          >
            <NavigateNext />
          </IconButton>
        </div>
        <div className={classes.calendarView}>{this.renderCalendar()}</div>
      </React.Fragment>
    );
  }
}

ApptCalendarDayView.propTypes = {
  history: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(withRouter(observer(ApptCalendarDayView)));
